import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Helper to determine primary dashboard based on user permissions
function getPrimaryDashboard(user: any): string {
  const hasFactoryAccess = user.allowed_portals?.includes('factory');
  const hasAccountingAccess = user.allowed_portals?.includes('accounting');

  // If user has factory access, go to main admin dashboard
  if (hasFactoryAccess) {
    return '/';
  }

  // If user only has accounting access, go to accounting dashboard
  if (hasAccountingAccess) {
    return '/accounting/dashboard';
  }

  // Fallback to main dashboard
  return '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Get user profile to determine where to route
      const user = await api.getProfile();
      navigate(getPrimaryDashboard(user));
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
            alt="Keder Screens"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">Factory Admin</h1>
          <p className="text-gray-400 mt-2">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-[#FFCD24] font-medium mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg bg-[#111] border border-[#333] text-white placeholder-gray-500 focus:border-[#FFCD24] focus:outline-none focus:ring-2 focus:ring-[#FFCD24]/30"
                placeholder="Email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-[#FFCD24] font-medium mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-lg bg-[#111] border border-[#333] text-white placeholder-gray-500 focus:border-[#FFCD24] focus:outline-none focus:ring-2 focus:ring-[#FFCD24]/30"
                placeholder="••••••••"
              />
            </div>

            <div className="flex justify-end">
              <a href="#" className="text-sm text-[#FFCD24] hover:underline">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFCD24] hover:bg-yellow-400 text-black font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#333] text-center">
            <p className="text-gray-400 text-sm">
              Need an account?{' '}
              <a href="mailto:support@kederscreens.com" className="text-[#FFCD24] hover:underline">
                Contact us
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          © {new Date().getFullYear()} Keder Screens. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
